import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import MobileMenu from "./MenuIcon";
import { useAuth } from "../contexts/AuthContext";
import houses from "../pages/HouseInfo";

const NavBarContainer = styled.nav`
  padding-left: 3rem;
  padding-right: 3rem;
  height: 10vh;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.primaryColor};
  background-color: ${({ theme }) => theme.backgroundColors.primary};
  display: flex;
  flex-direction: row;
  gap: 1rem;
  @media (max-width: 768px) {
    padding-top: 0.5rem;
    padding-left: 10px;
    padding-right: 10px;
  }
`;

const NavBarLinksContainer = styled.div`
  align-items: center;
  justify-content: flex-end;
  display: flex;
  gap: 1.5rem;
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavBarItemContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.backgroundColors.primary};
  transition: color 0.2s ease, background-color 0.2s ease;

  a {
    font-family: ${({ theme }) => theme.fonts.primary};
    color: ${({ theme }) => theme.textColors.primary};
    text-decoration: none;
    font-size: 1.2rem;
    transition: color 0.2s ease, background-color 0.5s ease;
  }

  &:hover > div {
    display: flex;
  }
`;

const DropdownMenu = styled.div`
  display: none;
  position: absolute;
  top: 100%; // This positions the dropdown right below the link
  // right: 5px; // Aligns the dropdown to the start of the NavBarItemContainer
  transform: translateX(
    -10%
  ); // Shifts the dropdown to the left; adjust as needed
  flex-direction: column;
  background-color: ${({ theme }) => theme.backgroundColors.primary};
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1100;
  min-width: 160px; // Ensure it's wide enough to hold the content
  border: 1px solid ${({ theme }) => theme.textColors.secondary};
`;

const PropertiesItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.backgroundColors.primary};

  a {
    font-family: ${({ theme }) => theme.fonts.primary};
    color: ${({ theme }) => theme.textColors.primary};
    text-decoration: none;
    font-size: 1.2rem;
  }
`;

const PropertiesTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1.2rem;
  color: ${({ theme }) => theme.textColors.primary};

  .caret {
    font-size: 0.7rem;
    line-height: 1;
    transition: transform 0.2s ease;
  }

  &[aria-expanded="true"] .caret {
    transform: rotate(180deg);
  }
`;

/* The owner menu opens on hover alone, which no keyboard can reach. This one
   is driven by state so click and focus work too. */
const PropertiesMenu = styled(DropdownMenu)<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? "flex" : "none")};
  transform: none;
  left: 0;
  min-width: 240px;

  a {
    display: block;
    width: 100%;
    font-size: 1rem;
  }
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  font-size: 1rem;
  color: ${({ theme }) => theme.textColors.primary};
  &:hover {
    background-color: ${({ theme }) => theme.backgroundColors.secondary};
  }
`;

const NavBarPhotoContainer = styled.div`
  flex: 1;
`;

const StyledImg = styled.img`
  max-width: 200px;

  @media (max-width: 768px) {
    max-width: 150px;
  }
`;

const OwnerViewLink = styled(NavBarItemContainer)`
  border: 1px solid #5f8c95;
  padding: 5px;
  border-radius: 5px;

  &:hover {
    background-color: ${({ theme }) => theme.backgroundColors.secondary};
  }

  &:hover > a {
    color: ${({ theme }) => theme.textColors.tertiary};
  }

  // Ensure DropdownMenu does not inherit the hover background color
  ${DropdownMenu} {
    background-color: ${({ theme }) =>
      theme.backgroundColors.primary} !important;
  }
`;

interface NavBarProps {
  logoSrc?: string; // Optional prop
}

function NavBar({ logoSrc = "../horizontal_logo.jpg" }: NavBarProps) {
  const { isLogged, logout } = useAuth();
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const propertiesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!propertiesOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!propertiesRef.current?.contains(event.target as Node)) {
        setPropertiesOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPropertiesOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [propertiesOpen]);

  return (
    <NavBarContainer>
      <NavBarPhotoContainer>
        <Link to="/">
          <StyledImg src={logoSrc} alt="logo" />
        </Link>
      </NavBarPhotoContainer>

      <NavBarLinksContainer>
        <NavBarItemContainer>
          <Link to="/">Home</Link>
        </NavBarItemContainer>

        <PropertiesItem
          ref={propertiesRef}
          onMouseEnter={() => setPropertiesOpen(true)}
          onMouseLeave={() => setPropertiesOpen(false)}
        >
          <PropertiesTrigger
            type="button"
            aria-expanded={propertiesOpen}
            aria-haspopup="true"
            onClick={() => setPropertiesOpen((open) => !open)}
          >
            Properties <span className="caret">▼</span>
          </PropertiesTrigger>

          <PropertiesMenu $open={propertiesOpen}>
            {houses.map((house) => (
              <DropdownItem key={house.propertyId}>
                <Link
                  to={house.route}
                  onClick={() => setPropertiesOpen(false)}
                >
                  {house.displayName}
                </Link>
              </DropdownItem>
            ))}
          </PropertiesMenu>
        </PropertiesItem>

        <NavBarItemContainer>
          <Link to="/availability">Availability</Link>
        </NavBarItemContainer>

        <NavBarItemContainer>
          <Link to="/contact">Contact Yiping Ding</Link>
        </NavBarItemContainer>
        <OwnerViewLink
          style={{
            border: "1px solid #5F8C95",
            padding: "5px",
            borderRadius: "5px",
          }}
        >
          {isLogged ? (
            <>
              <Link to="/owner-view">Owner View</Link>
              <DropdownMenu>
                <DropdownItem>
                  <Link to="/owner-view">Owner View</Link>
                </DropdownItem>
                <DropdownItem
                  onClick={logout}
                  style={{
                    cursor: "pointer",
                    fontSize: "1.2rem",
                  }}
                >
                  Logout
                </DropdownItem>
              </DropdownMenu>
            </>
          ) : (
            <Link to="/login">Owner Login</Link>
          )}
        </OwnerViewLink>
      </NavBarLinksContainer>
      <MobileMenu />
    </NavBarContainer>
  );
}

export default NavBar;
